import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { apiService } from '../utils/api';
import { LeadGenResponse } from '../types/api';
import useFormStore from '../state/useFormStore';

export const useLeadGen = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [showValidation, setShowValidation] = useState(false);
  const [leadGenHash, setLeadGenHash] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    setHash,
    setNames,
    setCuilParameter,
    setEmploymentParameter,
    setDniData,
    setEmailData,
    setPhoneData,
    setBankData,
    setFirstNameData,
    setLastNameData,
    setGenderData,
    setBirthdateData,
    setPreOffers,
    setRequestUid,
    setSelectedOffer
  } = useFormStore();

  useEffect(() => {
    const hashParam = searchParams.get('hash');
    
    if (hashParam) {
      setLeadGenHash(hashParam);
      setShowValidation(true);
    }
  }, [searchParams]);

  const processArgentinianName = (fullName: string): { firstname: string; lastname: string } => {
    // Eliminar espacios extras y dividir por espacios
    const nameParts = fullName.trim().replace(/\s+/g, ' ').split(' ');
    
    // Si solo hay dos palabras, la primera es nombre y la segunda apellido
    if (nameParts.length === 2) {
      return {
        firstname: nameParts[0],
        lastname: nameParts[1]
      };
    }
    
    // Si hay 3 o más palabras
    if (nameParts.length >= 3) {
      // Las dos primeras palabras son nombres en la mayoría de los casos argentinos
      const firstname = nameParts.slice(0, 2).join(' ');
      // El resto son apellidos
      const lastname = nameParts.slice(2).join(' ');
      
      return { firstname, lastname };
    }
    
    // Si solo hay una palabra (caso poco común)
    return {
      firstname: nameParts[0],
      lastname: nameParts[0]
    };
  };

  const handleValidationSuccess = (data?: LeadGenResponse) => {
    console.log('LeadGen - handleValidationSuccess llamado con:', { data });
    try {
      // Procesar los datos recibidos como parámetro
      if (data && data.data) {
        console.log('LeadGen - Procesando data recibida');
        processLeadGenData(data);
      } else {
        console.log('LeadGen - No hay datos válidos para procesar');
        throw new Error('No hay datos válidos para procesar');
      }
      // No cerrar el modal aquí, se cerrará después de la redirección
      setError(null);
    } catch (err) {
      console.error('LeadGen - Error en handleValidationSuccess:', err);
      setError(err instanceof Error ? err.message : 'Error procesando datos');
      handleValidationError();
    }
  };

  const handleValidationError = () => {
    console.log('LeadGen - Error al procesar datos');
    setShowValidation(false);
    setLeadGenHash(null);
    setError(null);
  };

  const processLeadGenData = (data: LeadGenResponse) => {
    console.log('LeadGen - processLeadGenData iniciado con:', data);
    
    const { uid, preOffers, selectedOffer, leadGeneration, employmentInfo, personalInfo } = data.data;
    // const applicant = data.requestStatus.applicant;

    console.log('LeadGen - Datos extraídos:', {
      uid,
      personalInfo,
      employmentInfo,
      // applicant
    });

    // if (!applicant) {
    //   throw new Error('Datos del solicitante no disponibles');
    // }

    if (!personalInfo) {
      throw new Error('Datos personales no disponibles');
    }

    // Usar los datos estructurados de personalInfo en lugar de procesar el nombre
    const firstName = personalInfo.firstName;
    const lastName = personalInfo.lastName;
    const fullName = `${firstName} ${lastName}`;

    console.log('LeadGen - Datos procesados:', {
      personalInfo: personalInfo,
      firstName: firstName,
      lastName: lastName,
      fullName: fullName,
      employmentInfo: employmentInfo,
      bank: employmentInfo?.bank
    });

    console.log('LeadGen - Antes de guardar en store');
    
    // Guardar datos en el store (igual que handleConfirm)
    setHash(uid);
    setRequestUid(uid);
    setNames(fullName);
    setCuilParameter(personalInfo.cuil); // Usar el CUIL real de personalInfo
    setEmploymentParameter(employmentInfo?.employment_status?.code || 'activo'); // Usar el código real del empleo
    setDniData(personalInfo.dni); // Usar el DNI real de personalInfo
    // setEmailData(applicant.email);
    setPhoneData(''); // En lead gen no tenemos teléfono inicialmente
    setFirstNameData(firstName); // Usar firstName de personalInfo
    setLastNameData(lastName); // Usar lastName de personalInfo
    // setGenderData(applicant.gender);
    setBankData(employmentInfo?.bank?.code || ''); // Usar el código real del banco
    setBirthdateData(''); // En lead gen no tenemos fecha de nacimiento inicialmente
    setPreOffers(preOffers);
    
    console.log('LeadGen - Después de guardar en store');
    
    if (selectedOffer) {
      setSelectedOffer({
        id: selectedOffer.id,
        amount: selectedOffer.max_amount,
        installmentAmount: selectedOffer.max_amount / selectedOffer.installments,
        rate: selectedOffer.rate,
        installments: selectedOffer.installments,
        firstDueDate: selectedOffer.first_due_date
      });
    }

    // Guardar en localStorage (igual que handleConfirm)
    const userData = {
      hash: uid,
      requestUid: uid,
      dni: personalInfo.dni, // Usar DNI de personalInfo
      // email: applicant.email,
      phone: '', // En lead gen no tenemos teléfono inicialmente
      employment: employmentInfo?.employment_status?.code || null, // Usar el código real del empleo
      bank: employmentInfo?.bank?.code || '', // Usar el código real del banco
      submittedAt: new Date().toISOString(),
      birthdate: '', // En lead gen no tenemos fecha de nacimiento inicialmente
      cuit: personalInfo.cuil, // Usar CUIL de personalInfo
      // gender: applicant.gender,
      name: fullName, // Nombre completo construido
      first_name: firstName, // Usar firstName de personalInfo
      very_first_name: firstName.split(' ')[0], // Solo el primer nombre
      last_name: lastName, // Usar lastName de personalInfo
      preOffers: preOffers,
      selectedOffer: selectedOffer,
      leadGeneration: leadGeneration,
      employmentInfo: employmentInfo // Guardar toda la información de empleo
    };
    
    console.log('LeadGen - Datos guardados en localStorage:', {
      first_name: userData.first_name,
      very_first_name: userData.very_first_name,
      name: userData.name,
      dni: userData.dni,
      cuit: userData.cuit,
      employment: userData.employment,
      bank: userData.bank
    });
    
    localStorage.setItem(`prestix_${uid}`, JSON.stringify(userData));

    console.log('LeadGen - Antes de cerrar modal y redirigir');
    
    // Cerrar el modal y redirigir
    setShowValidation(false);
    setLeadGenHash(null);
    navigate(`/${uid}/ingresos`); // nos salteamos la pre-oferta, ya que la vio en el lead gen
  };

  const validateLeadGen = async (hash: string, dniLastDigits: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiService.requests.getFromLeadGen(hash, dniLastDigits);
      const data = response.data;
      
      // Si la respuesta es exitosa (status 200), procesar los datos
      if (response.status === 200 && data.data && data.requestStatus) {
        // Retornar los datos validados para que el componente los use
        console.log('LeadGen - Datos validados exitosamente:', data);
        return data;
      } else {
        throw new Error('Error al cargar datos de lead generation');
      }
    } catch (error: any) {
      console.error('Error validating lead gen:', error);
      
      const errorCode = error.response?.data?.error?.code;
      let errorMessage = 'Error al cargar los datos. Serás redirigido al inicio.';
      let shouldAllowRetry = false;
      let shouldShowNewFlowButton = false;
      let shouldAutoRedirect = false;
      
      switch (errorCode) {
        case 'VALIDATION_ERROR':
          errorMessage = 'Ingresa exactamente 3 dígitos';
          shouldAllowRetry = true;
          break;
          
        case 'REQUEST004':
          errorMessage = 'Solicitud no encontrada';
          shouldShowNewFlowButton = true;
          break;
          
        case 'REQUEST009':
          errorMessage = 'No es de lead generation';
          shouldShowNewFlowButton = true;
          break;
          
        case 'REQUEST010':
          errorMessage = 'Sin oferta seleccionada';
          shouldShowNewFlowButton = true;
          break;
          
        case 'REQUEST_ALREADY_REDEEMED':
          errorMessage = 'Esta oferta ya fue redimida';
          shouldShowNewFlowButton = true;
          break;
          
        case 'REQUEST_EXPIRED':
          errorMessage = 'Esta oferta ha expirado';
          shouldShowNewFlowButton = true;
          break;
          
        case 'REQUEST_CANNOT_BE_REDEEMED':
          errorMessage = 'Esta oferta no puede ser redimida';
          shouldShowNewFlowButton = true;
          break;
          
        case 'DNI_VALIDATION_FAILED':
          errorMessage = 'Los dígitos no coinciden';
          shouldAllowRetry = true;
          break;
          
        default:
          errorMessage = 'Error al cargar los datos. Serás redirigido al inicio.';
          shouldAutoRedirect = true;
          break;
      }
      
      // Crear un objeto de error personalizado con información adicional
      const customError = new Error(errorMessage);
      (customError as any).errorCode = errorCode;
      (customError as any).shouldAllowRetry = shouldAllowRetry;
      (customError as any).shouldShowNewFlowButton = shouldShowNewFlowButton;
      (customError as any).shouldAutoRedirect = shouldAutoRedirect;
      
      setError(errorMessage);
      throw customError;
    } finally {
      setLoading(false);
    }
  };

  return {
    showValidation,
    leadGenHash,
    loading,
    error,
    handleValidationSuccess,
    handleValidationError,
    validateLeadGen
  };
};
